import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

import { BaseButton, CloseButton, useIsMobile, useT } from '@repo/common';
import PreviewDownloadModal from './PreviewDownloadModal';
import PreviewPagedContent from './PreviewPagedContent';

import { ReactComponent as DownloadIcon } from '@repo/common/assets/icons/16px/icon_16px_download.svg';
import { ReactComponent as ZoomInIcon } from '@repo/common/assets/icons/16px/icon_16px_zoom-in.svg';
import { ReactComponent as ZoomOutIcon } from '@repo/common/assets/icons/16px/icon_16px_zoom-out.svg';
import { ReactComponent as FullScreenIcon } from '@repo/common/assets/icons/16px/icon_16px_fullscreen.svg';

const PreviewLargerModal = ({
  resumeData,
  onClose,
  onDownload,
  isApply,
  isCareer = false,
}) => {
  const isMobile = useIsMobile();
  const t = useT();
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const zoomLevels = [50, 75, 100, 125, 150, 200];
  const [zoomIndex, setZoomIndex] = useState(2);
  const [currentZoom, setCurrentZoom] = useState(100);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [enableTransition, setEnableTransition] = useState(false);

  const containerRef = useRef(null);

  const handleZoomIn = () => {
    setIsFullScreen(false);
    const nextIndex = Math.min(zoomIndex + 1, zoomLevels.length - 1);
    setZoomIndex(nextIndex);
    setCurrentZoom(zoomLevels[nextIndex]);
  };

  const handleZoomOut = () => {
    if (isFullScreen) {
      const lastIndex = zoomLevels.length - 1;
      setZoomIndex(lastIndex);
      setCurrentZoom(zoomLevels[lastIndex]);
      setIsFullScreen(false);
    } else {
      const nextIndex = Math.max(zoomIndex - 1, 0);
      setZoomIndex(nextIndex);
      setCurrentZoom(zoomLevels[nextIndex]);
    }
  };

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useLayoutEffect(() => {
    if (!isMobile || !containerRef.current) return;

    const padding = 32;
    const availableWidth = containerRef.current.clientWidth - padding;
    const fitZoom = Math.floor((availableWidth / 595) * 100);

    setCurrentZoom(fitZoom);

    const closestIndex = zoomLevels.reduce(
      (prev, curr, idx) =>
        Math.abs(curr - fitZoom) < Math.abs(zoomLevels[prev] - fitZoom)
          ? idx
          : prev,
      0,
    );
    setZoomIndex(closestIndex);

    requestAnimationFrame(() => setEnableTransition(true));
  }, [isMobile]);

  const handleFullScreenToggle = () => {
    if (!containerRef.current) return;

    if (!isFullScreen) {
      const padding = 64;
      const availableWidth = containerRef.current.clientWidth - padding;
      const fitZoom = Math.floor((availableWidth / 595) * 100);

      setCurrentZoom(fitZoom);
      setIsFullScreen(true);

      const closestIndex = zoomLevels.reduce(
        (prev, curr, idx) =>
          Math.abs(curr - fitZoom) < Math.abs(zoomLevels[prev] - fitZoom)
            ? idx
            : prev,
        0,
      );
      setZoomIndex(closestIndex);
    } else {
      const lastIndex = zoomLevels.length - 1;
      setZoomIndex(lastIndex);
      setCurrentZoom(zoomLevels[lastIndex]);
      setIsFullScreen(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className={
        isCareer
          ? 'fixed inset-0 z-[10000000] bg-grayscale-gray01'
          : `fixed inset-0 z-[10000000] flex items-center justify-center bg-surface-black40 ${
              isMobile ? 'p-0' : 'p-4 tabletH:p-6 tabletV:p-10 desktopL:p-20'
            }`
      }
    >
      {!isCareer && <div className='absolute inset-0' onClick={onClose} />}

      <div
        ref={containerRef}
        className={
          isCareer
            ? 'relative flex flex-col w-full h-full bg-grayscale-gray01 overflow-hidden'
            : `relative flex flex-col w-full h-full bg-grayscale-gray01 overflow-hidden ${
                isMobile ? 'rounded-none px-4' : 'rounded-[30px] px-10'
              }`
        }
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {!isCareer && (
          <div className='w-full flex flex-row-reverse tabletH:flex-row items-center justify-between py-1.5 tabletH:pt-6 tabletH:pb-4 h-[54px] tabletH:h-auto bg-grayscale-gray01'>
            {isMobile && <div className='w-6 h-6' />}
            <div className='mx-auto typo-b16Strong text-text-primary'>
              {t('Resume Preview', '이력서 미리보기')}
            </div>
            <CloseButton type='default' onClick={onClose} />
          </div>
        )}

        <div className='flex-1 overflow-auto flex items-start p-4 bg-grayscale-gray02 min-h-0'>
          <div
            className='mx-auto'
            style={{
              width: `${(595 * currentZoom) / 100}px`,
              height: `${(842 * currentZoom) / 100}px`,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                transform: `scale(${currentZoom / 100})`,
                transformOrigin: 'top left',
                transition: enableTransition
                  ? 'transform 0.2s ease-out'
                  : 'none',
                width: '595px',
                height: '842px',
              }}
            >
              <PreviewPagedContent resumeData={resumeData} />
            </div>
          </div>
        </div>

        <div className='flex items-center justify-center gap-2 pb-[30px] pt-6'>
          {!isApply && (
            <BaseButton
              type='text'
              size='s'
              icon={<DownloadIcon />}
              bgclass='bg-grayscale-gray00 border-grayscale-gray02 h-[32px]'
              width='w-[71px]'
              onClick={() => setIsDownloadOpen(true)}
            >
              PDF
            </BaseButton>
          )}

          <div className='flex items-center gap-3'>
            <BaseButton
              type='text'
              size='s'
              icon={<ZoomOutIcon />}
              bgclass='bg-grayscale-gray00 border-grayscale-gray02 h-[32px]'
              width='w-[71px]'
              onClick={handleZoomOut}
            />
            <span className='typo-b14Strong min-w-[45px] text-center'>
              {isFullScreen ? t('Full', '전체 화면') : `${currentZoom}%`}
            </span>
            <BaseButton
              type='text'
              size='s'
              icon={<ZoomInIcon />}
              bgclass='bg-grayscale-gray00 border-grayscale-gray02 h-[32px]'
              width='w-[71px]'
              onClick={handleZoomIn}
            />
          </div>

          <BaseButton
            type='text'
            size='s'
            icon={<FullScreenIcon />}
            bgclass='bg-grayscale-gray00 border-grayscale-gray02 h-[32px]'
            width='w-[71px]'
            onClick={handleFullScreenToggle}
          />
        </div>
      </div>

      {isDownloadOpen && (
        <PreviewDownloadModal
          onClose={() => setIsDownloadOpen(false)}
          onDownload={onDownload}
          hideDim
        />
      )}
    </div>,
    document.body,
  );
};

export default PreviewLargerModal;
